// src/pages/Proposals/ProposalDetailPage.js

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  getDisplayUnit,
  normalizeFilesUnits,
  DEFAULT_UNITS,
} from "../../utils/units";

// Proposals
import { fetchProposalById, exportProposalsById } from "api/proposalApi";

import {
  fileFetchByModelKey,
  fileListByModelKeyAndProposal,
} from "api/FileApiRegistry";

import { loadLastFileByModelKey } from "utils/fileLoaders";

// CMAM TOFERDA
import {
  fetchCMAMTOFERDAFileById,
  exportCMAMTOFERDAFilesById,
  softDeleteCMAMTOFERDAFile,
} from "api/CMAMTOFERDAFileApi";

// CNA STD ERDA
import {
  fetchCNASTDERDAFileById,
  exportCNASTDERDAFilesById,
  softDeleteCNASTDERDAFile,
} from "api/CNASTDERDAFileApi";

// CNA STD NRA
import {
  fetchCNASTDNRAFileById,
  exportCNASTDNRAFilesById,
  softDeleteCNASTDNRAFile,
} from "api/CNASTDNRAFileApi";

// CNA STD PIGE
import {
  fetchCNASTDPIGEFileById,
  exportCNASTDPIGEFilesById,
  softDeleteCNASTDPIGEFile,
} from "api/CNASTDPIGEFileApi";

// CNA STD PIXE
import {
  fetchCNASTDPIXEFileById,
  exportCNASTDPIXEFilesById,
  softDeleteCNASTDPIXEFile,
} from "api/CNASTDPIXEFileApi";

// CNA STD RBS
import {
  fetchCNASTDRBSFileById,
  exportCNASTDRBSFilesById,
  softDeleteCNASTDRBSFile,
} from "api/CNASTDRBSFileApi";

// CNA uB IBIC
import {
  fetchCNAuBIBICFileById,
  exportCNAuBIBICFilesById,
  softDeleteCNAuBIBICFile,
} from "api/CNAuBIBICFileApi";

// CNA uB PIXE
import {
  fetchCNAuBPIXEFileById,
  exportCNAuBPIXEFilesById,
  softDeleteCNAuBPIXEFile,
} from "api/CNAuBPIXEFileApi";

// CNA uB TRIBIC
import {
  fetchCNAuBTRIBICFileById,
  exportCNAuBTRIBICFilesById,
  softDeleteCNAuBTRIBICFile,
} from "api/CNAuBTRIBICFileApi";

import FileDetailView from "./FileDetailView";
import FileDialog from "./FileDialog/FileDialog";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const modelLabels = {
  cna_std_rbs_files: "RBS",
  cna_std_erda_files: "ERDA",
  cna_std_nra_files: "NRA",
  cna_std_pixe_files: "PIXE",
  cna_std_pige_files: "PIGE",
  cmam_toferda_files: "ERDA-TOF",
  cna_ub_ibic_files: "IBIC",
  cna_ub_pixe_files: "PIXE",
  cna_ub_tribic_files: "TRIBIC",
};

const beamlineToModels = {
  "CNA-STD": [
    "cna_std_erda_files",
    "cna_std_nra_files",
    "cna_std_pige_files",
    "cna_std_pixe_files",
    "cna_std_rbs_files",
  ],
  "CMAM-ERDA-TOF": ["cmam_toferda_files"],
  "CNA-MIC": ["cna_ub_ibic_files", "cna_ub_pixe_files", "cna_ub_tribic_files"],
};

const getColumnsForModel = (modelKey, openFileDetail) => {
  switch (modelKey) {
    case "cmam_toferda_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.7 },
        { field: "sample_ID", headerName: "Sample ID", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.4,
          valueGetter: (params) => {
            const beam_isotope = params;
            return beam_isotope?.symbol && beam_isotope?.mass_number
              ? `${beam_isotope.symbol} - ${beam_isotope.mass_number}`
              : "—";
          },
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.55 },
        { field: "beam_energy", headerName: "Energy", flex: 0.4 },
        { field: "beam_energy_units", headerName: "Units", flex: 0.3 },
        { field: "beam_current", headerName: "Current", flex: 0.4 },
        { field: "beam_current_units", headerName: "Units", flex: 0.3 },
        { field: "incident_angle", headerName: "Incident Angle", flex: 0.6 },
        { field: "incident_angle_units", headerName: "Units", flex: 0.3 },
        { field: "exit_angle", headerName: "Exit Angle", flex: 0.5 },
        { field: "exit_angle_units", headerName: "Units", flex: 0.3 },
        {
          field: "TOF_detector",
          headerName: "TOF detector",
          flex: 0.5,
          valueGetter: (params) => {
            const TOF_detector = params;
            return TOF_detector?.name ?? "—";
          },
        },
        {
          field: "TOF_station_BIAS_voltage",
          headerName: "TOF BIAS",
          flex: 0.4,
        },
        {
          field: "TOF_station_BIAS_voltage_units",
          headerName: "Units",
          flex: 0.3,
        },
        {
          field: "energy_detector",
          headerName: "Detector",
          flex: 0.4,
          valueGetter: (params) => {
            const energy_detector = params;
            return energy_detector?.name ?? "—";
          },
        },
        {
          field: "energy_detector_BIAS_voltage",
          headerName: "BIAS",
          flex: 0.3,
        },
        {
          field: "energy_detector_BIAS_voltage_units",
          headerName: "Units",
          flex: 0.3,
        },
        { field: "coincidence_window", headerName: "Coin. Wind.", flex: 0.5 },
        { field: "coincidence_window_units", headerName: "Units", flex: 0.3 },
        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params), // asegura un objeto Date
        },
        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];
    case "cna_std_erda_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.5 },
        { field: "sample_ID", headerName: "Sample ID", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.35,
          valueGetter: (params) => {
            const beam_isotope = params;
            return beam_isotope?.symbol && beam_isotope?.mass_number
              ? `${beam_isotope.symbol} - ${beam_isotope.mass_number}`
              : "—";
          },
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.4 },
        { field: "beam_energy", headerName: "Energy", flex: 0.35 },
        // { field: "beam_energy_units", headerName: "Units", flex: 0.25 },
        { field: "beam_current", headerName: "Current", flex: 0.35 },
        // { field: "beam_current_units", headerName: "Units", flex: 0.25 },

        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        // { field: "injection_energy_units", headerName: "Units", flex: 0.25 },

        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.38 },
        // { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },

        { field: "magnetic_field", headerName: "B₉₀°", flex: 0.35 },
        // { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },

        { field: "incident_angle", headerName: "Inc. Angle", flex: 0.4 },
        // { field: "incident_angle_units", headerName: "Units", flex: 0.25 },
        { field: "exit_angle", headerName: "Exit Angle", flex: 0.4 },
        // { field: "exit_angle_units", headerName: "Units", flex: 0.25 },
        { field: "scattering_angle", headerName: "Scatt. Angle", flex: 0.5 },
        // { field: "scattering_angle_units", headerName: "Units", flex: 0.25 },

        { field: "accumulated_charge", headerName: "Q", flex: 0.15 },
        // { field: "accumulated_charge_units", headerName: "Units", flex: 0.2 },

        {
          field: "is_filter_present",
          headerName: "Filt.?",
          flex: 0.3,
          valueGetter: (params) =>
            params === true ? "Yes" : params === false ? "No" : "—",
        },
        {
          field: "filter_material",
          headerName: "Mat.",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness",
          headerName: "Thickness",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        // {
        //   field: "filter_thickness_units",
        //   headerName: "Units",
        //   flex: 0.25,
        //   valueGetter: (params) => params || "—",
        // },

        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params), // asegura un objeto Date
        },

        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];

    case "cna_std_nra_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.4 },
        { field: "sample_ID", headerName: "Sample ID", flex: 0.4 },
        {
          field: "reaction",
          headerName: "Reaction",
          flex: 0.55,
          valueGetter: (params) => {
            const reaction = params;
            return reaction?.reaction_notation
              ? `${reaction?.reaction_notation}`
              : "—";
          },
        },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.35,
          valueGetter: (params) => {
            const beam_isotope = params;
            return beam_isotope?.symbol && beam_isotope?.mass_number
              ? `${beam_isotope.symbol} - ${beam_isotope.mass_number}`
              : "—";
          },
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.41 },
        { field: "beam_energy", headerName: "Energy", flex: 0.35 },
        { field: "beam_energy_units", headerName: "Units", flex: 0.26 },
        { field: "beam_current", headerName: "Current", flex: 0.4 },
        { field: "beam_current_units", headerName: "Units", flex: 0.25 },

        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        { field: "injection_energy_units", headerName: "Units", flex: 0.26 },

        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.35 },
        { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },

        { field: "magnetic_field", headerName: "B₉₀°", flex: 0.35 },
        { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },

        { field: "incident_angle", headerName: "Inc. Angle", flex: 0.3 },
        { field: "incident_angle_units", headerName: "Units", flex: 0.25 },
        { field: "exit_angle", headerName: "Exit Angle", flex: 0.3 },
        { field: "exit_angle_units", headerName: "Units", flex: 0.25 },
        { field: "scattering_angle", headerName: "Scatt. Angle", flex: 0.4 },
        { field: "scattering_angle_units", headerName: "Units", flex: 0.25 },

        { field: "accumulated_charge", headerName: "Q", flex: 0.15 },
        { field: "accumulated_charge_units", headerName: "Units", flex: 0.2 },

        {
          field: "is_filter_present",
          headerName: "Filt.?",
          flex: 0.27,
          valueGetter: (params) =>
            params === true ? "Yes" : params === false ? "No" : "—",
        },
        {
          field: "filter_material",
          headerName: "Mat.",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness",
          headerName: "Thickness",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness_units",
          headerName: "Units",
          flex: 0.25,
          valueGetter: (params) => params || "—",
        },

        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params), // asegura un objeto Date
        },

        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];
    case "cna_std_pige_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.5 },
        { field: "sample_ID", headerName: "Sample ID", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.3,
          valueGetter: (params) => {
            const beam_isotope = params;
            return beam_isotope?.symbol && beam_isotope?.mass_number
              ? `${beam_isotope.symbol} - ${beam_isotope.mass_number}`
              : "—";
          },
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.5 },
        { field: "beam_energy", headerName: "Energy", flex: 0.35 },
        { field: "beam_energy_units", headerName: "Units", flex: 0.25 },
        { field: "beam_current", headerName: "Current", flex: 0.4 },
        { field: "beam_current_units", headerName: "Units", flex: 0.25 },

        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        { field: "injection_energy_units", headerName: "Units", flex: 0.25 },

        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.35 },
        { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },

        { field: "magnetic_field", headerName: "B₉₀°", flex: 0.35 },
        { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },

        { field: "incident_angle", headerName: "Inc. Angle", flex: 0.4 },
        { field: "incident_angle_units", headerName: "Units", flex: 0.25 },
        { field: "exit_angle", headerName: "Exit Angle", flex: 0.4 },
        { field: "exit_angle_units", headerName: "Units", flex: 0.25 },
        { field: "scattering_angle", headerName: "Scatt. Angle", flex: 0.5 },
        { field: "scattering_angle_units", headerName: "Units", flex: 0.25 },

        { field: "accumulated_charge", headerName: "Q", flex: 0.35 },
        { field: "accumulated_charge_units", headerName: "Units", flex: 0.3 },

        {
          field: "is_filter_present",
          headerName: "Filt.?",
          flex: 0.3,
          valueGetter: (params) =>
            params === true ? "Yes" : params === false ? "No" : "—",
        },
        {
          field: "filter_material",
          headerName: "Mat.",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness",
          headerName: "Thickness",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness_units",
          headerName: "Units",
          flex: 0.25,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_diameter",
          headerName: "Diameter",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_diameter_units",
          headerName: "Units",
          flex: 0.25,
          valueGetter: (params) => params || "—",
        },

        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params), // asegura un objeto Date
        },

        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];

    case "cna_std_pixe_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.5 },
        { field: "sample_ID", headerName: "Sample ID", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.3,
          valueGetter: (params) => {
            const beam_isotope = params;
            return beam_isotope?.symbol && beam_isotope?.mass_number
              ? `${beam_isotope.symbol} - ${beam_isotope.mass_number}`
              : "—";
          },
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.5 },
        { field: "beam_energy", headerName: "Energy", flex: 0.35 },
        { field: "beam_energy_units", headerName: "Units", flex: 0.25 },
        { field: "beam_current", headerName: "Current", flex: 0.4 },
        { field: "beam_current_units", headerName: "Units", flex: 0.25 },

        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        { field: "injection_energy_units", headerName: "Units", flex: 0.25 },

        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.35 },
        { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },

        { field: "magnetic_field", headerName: "B₉₀°", flex: 0.35 },
        { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },

        { field: "incident_angle", headerName: "Inc. Angle", flex: 0.4 },
        { field: "incident_angle_units", headerName: "Units", flex: 0.25 },
        { field: "exit_angle", headerName: "Exit Angle", flex: 0.4 },
        { field: "exit_angle_units", headerName: "Units", flex: 0.25 },
        { field: "scattering_angle", headerName: "Scatt. Angle", flex: 0.5 },
        { field: "scattering_angle_units", headerName: "Units", flex: 0.25 },

        { field: "accumulated_charge", headerName: "Q", flex: 0.15 },
        { field: "accumulated_charge_units", headerName: "Units", flex: 0.2 },

        {
          field: "is_filter_present",
          headerName: "Filt.?",
          flex: 0.3,
          valueGetter: (params) =>
            params === true ? "Yes" : params === false ? "No" : "—",
        },
        {
          field: "filter_material",
          headerName: "Mat.",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness",
          headerName: "Thickness",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness_units",
          headerName: "Units",
          flex: 0.25,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_diameter",
          headerName: "Diameter",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_diameter_units",
          headerName: "Units",
          flex: 0.25,
          valueGetter: (params) => params || "—",
        },

        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params), // asegura un objeto Date
        },

        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];
    case "cna_std_rbs_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.5 },
        { field: "sample_ID", headerName: "Sample ID", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.3,
          valueGetter: (params) => {
            const beam_isotope = params;
            return beam_isotope?.symbol && beam_isotope?.mass_number
              ? `${beam_isotope.symbol} - ${beam_isotope.mass_number}`
              : "—";
          },
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.5 },
        { field: "beam_energy", headerName: "Energy", flex: 0.35 },
        // { field: "beam_energy_units", headerName: "Units", flex: 0.25 },
        { field: "beam_current", headerName: "Current", flex: 0.4 },
        // { field: "beam_current_units", headerName: "Units", flex: 0.25 },

        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        // { field: "injection_energy_units", headerName: "Units", flex: 0.25 },

        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.35 },
        // { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },

        { field: "magnetic_field", headerName: "B₉₀°", flex: 0.35 },
        // { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },

        { field: "incident_angle", headerName: "Inc. Angle", flex: 0.4 },
        // { field: "incident_angle_units", headerName: "Units", flex: 0.25 },
        { field: "exit_angle", headerName: "Exit Angle", flex: 0.4 },
        // { field: "exit_angle_units", headerName: "Units", flex: 0.25 },
        { field: "scattering_angle", headerName: "Scatt. Angle", flex: 0.5 },
        // { field: "scattering_angle_units", headerName: "Units", flex: 0.25 },

        { field: "accumulated_charge", headerName: "Q", flex: 0.15 },
        // { field: "accumulated_charge_units", headerName: "Units", flex: 0.2 },

        {
          field: "is_filter_present",
          headerName: "Filt.?",
          flex: 0.3,
          valueGetter: (params) =>
            params === true ? "Yes" : params === false ? "No" : "—",
        },
        {
          field: "filter_material",
          headerName: "Mat.",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        {
          field: "filter_thickness",
          headerName: "Thickness",
          flex: 0.3,
          valueGetter: (params) => params || "—",
        },
        // {
        //   field: "filter_thickness_units",
        //   headerName: "Units",
        //   flex: 0.25,
        //   valueGetter: (params) => params || "—",
        // },

        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params),
        },

        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];
    case "cna_ub_ibic_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.35,
          valueGetter: (params) =>
            params?.symbol && params?.mass_number
              ? `${params.symbol} - ${params.mass_number}`
              : "—",
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.4 },
        { field: "beam_energy", headerName: "Energy", flex: 0.35 },
        { field: "beam_energy_units", headerName: "Units", flex: 0.25 },
        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        { field: "injection_energy_units", headerName: "Units", flex: 0.25 },
        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.35 },
        { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },
        { field: "magnetic_field", headerName: "B", flex: 0.35 },
        { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },
        { field: "high_voltage_source", headerName: "HV Source", flex: 0.5 },
        {
          field: "is_preamplifier_modified",
          headerName: "Preamp Mod.",
          flex: 0.4,
          valueGetter: (params) =>
            params === true ? "Yes" : params === false ? "No" : "—",
        },
        { field: "preamplifier_ID", headerName: "Preamplifier", flex: 0.5 },
        {
          field: "amplifier_coarse_gain",
          headerName: "Coarse Gain",
          flex: 0.35,
        },
        { field: "amplifier_fine_gain", headerName: "Fine Gain", flex: 0.35 },
        { field: "ADC_ID", headerName: "ADC", flex: 0.5 },
        { field: "scan_size", headerName: "Scan Size", flex: 0.35 },
        { field: "scan_size_units", headerName: "Units", flex: 0.25 },
        { field: "time_per_pixel", headerName: "Time/px", flex: 0.35 },
        { field: "time_per_pixel_units", headerName: "Units", flex: 0.25 },
        { field: "count_rate", headerName: "Count Rate", flex: 0.35 },
        { field: "count_rate_units", headerName: "Units", flex: 0.25 },
        { field: "BIAS_voltage", headerName: "BIAS V", flex: 0.35 },
        { field: "BIAS_voltage_units", headerName: "Units", flex: 0.25 },
        { field: "incident_angle", headerName: "Angle", flex: 0.35 },
        { field: "incident_angle_units", headerName: "Units", flex: 0.25 },
        { field: "channel", headerName: "Channel", flex: 0.3 },
        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params),
        },
        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];
    case "cna_ub_pixe_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.5 },
        { field: "sample_ID", headerName: "Sample ID", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.35,
          valueGetter: (params) =>
            params?.symbol && params?.mass_number
              ? `${params.symbol} - ${params.mass_number}`
              : "—",
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.4 },
        {
          field: "beam_energy",
          headerName: `Energy (${getDisplayUnit("beam_energy_units")})`,
          flex: 0.35,
        },
        // { field: "beam_energy_units", headerName: "Units", flex: 0.25 },
        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        // { field: "injection_energy_units", headerName: "Units", flex: 0.25 },
        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.38 },
        // { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },
        { field: "magnetic_field", headerName: "B", flex: 0.35 },
        // { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },
        { field: "scan_size", headerName: "Scan Size", flex: 0.35 },
        // { field: "scan_size_units", headerName: "Units", flex: 0.25 },
        { field: "time_per_pixel", headerName: "Time/px", flex: 0.35 },
        // { field: "time_per_pixel_units", headerName: "Units", flex: 0.25 },
        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params),
        },
        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];
    case "cna_ub_tribic_files":
      return [
        { field: "name", headerName: "File Name", flex: 0.5 },
        { field: "initial_file", headerName: "Initial File", flex: 0.5 },
        { field: "final_file", headerName: "Final File", flex: 0.5 },
        {
          field: "beam_isotope",
          headerName: "Ion",
          flex: 0.35,
          valueGetter: (params) =>
            params?.symbol && params?.mass_number
              ? `${params.symbol} - ${params.mass_number}`
              : "—",
        },
        { field: "beam_charge_state", headerName: "Charge State", flex: 0.4 },
        { field: "beam_energy", headerName: "Energy", flex: 0.35 },
        { field: "beam_energy_units", headerName: "Units", flex: 0.25 },
        { field: "injection_energy", headerName: "Eᵢₙⱼ", flex: 0.25 },
        { field: "injection_energy_units", headerName: "Units", flex: 0.25 },
        { field: "terminal_voltage", headerName: "Vₜ", flex: 0.38 },
        { field: "terminal_voltage_units", headerName: "Units", flex: 0.25 },
        { field: "magnetic_field", headerName: "B", flex: 0.35 },
        { field: "magnetic_field_units", headerName: "Units", flex: 0.25 },
        { field: "amplifier", headerName: "Amplifier", flex: 0.5 },
        {
          field: "amplifier_coarse_gain",
          headerName: "Coarse Gain",
          flex: 0.35,
        },
        { field: "amplifier_fine_gain", headerName: "Fine Gain", flex: 0.35 },
        {
          field: "is_puntual_shot",
          headerName: "Punctual?",
          flex: 0.35,
          valueGetter: (params) =>
            params === true ? "Yes" : params === false ? "No" : "—",
        },
        { field: "scan_size", headerName: "Scan Size", flex: 0.35 },
        { field: "scan_size_units", headerName: "Units", flex: 0.25 },
        { field: "time_per_pixel", headerName: "Time/px", flex: 0.35 },
        { field: "time_per_pixel_units", headerName: "Units", flex: 0.25 },
        { field: "BIAS_voltage", headerName: "BIAS V", flex: 0.35 },
        { field: "BIAS_voltage_units", headerName: "Units", flex: 0.25 },
        { field: "oscilloscope_ID", headerName: "Oscilloscope", flex: 0.5 },
        { field: "averaging", headerName: "Averaging", flex: 0.35 },
        {
          field: "created_at",
          headerName: "Created At",
          flex: 0.3,
          type: "dateTime",
          valueGetter: (params) => new Date(params),
        },
        {
          field: "actions",
          headerName: "Actions",
          flex: 0.4,
          renderCell: (params) => (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => openFileDetail(modelKey, params.id)}
            >
              Details
            </Button>
          ),
        },
      ];

    default:
      return [
        { field: "name", headerName: "File Name", flex: 1 },
        { field: "beam_energy", headerName: "Energy", flex: 0.5 },
      ];
  }
};

const normalizeIfNeeded = (modelKey, files) => {
  if (
    modelKey !== "cna_ub_pixe_files" &&
    modelKey !== "cna_std_rbs_files" &&
    modelKey !== "cna_std_erda_files"
  )
    return files;
  console.log("Entrando a normalización");
  console.log("FILES:", files);

  const result = normalizeFilesUnits(files);

  console.log("RESULT:", result);

  return result;
};

const attachUnitsTogethers = (modelKey, columns) => {
  if (
    modelKey !== "cna_ub_pixe_files" &&
    modelKey !== "cna_std_rbs_files" &&
    modelKey !== "cna_std_erda_files"
  )
    return columns;

  return columns.map((col) => {
    const unitField = col.field + "_units";
    if (unitField in DEFAULT_UNITS) {
      return {
        ...col,
        headerName: `${
          col.headerName || col.field.replace(/_/g, " ")
        } (${getDisplayUnit(unitField)})`,
      };
    }
    return col;
  });
};

const ProposalDetailPage = () => {
  const { id } = useParams();
  const [proposal, setProposal] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [filesByType, setFilesByType] = useState({});
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedFilesByModel, setSelectedFilesByModel] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [addModelKey, setAddModelKey] = useState(null);
  const [addInitialValues, setAddInitialValues] = useState(null);

  const openFileDetail = async (modelKey, fileId) => {
    try {
      if (modelKey === "cmam_toferda_files") {
        const file = await fetchCMAMTOFERDAFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_std_erda_files") {
        const file = await fetchCNASTDERDAFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_std_nra_files") {
        const file = await fetchCNASTDNRAFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_std_pige_files") {
        const file = await fetchCNASTDPIGEFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_std_pixe_files") {
        const file = await fetchCNASTDPIXEFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_std_rbs_files") {
        const file = await fetchCNASTDRBSFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_ub_ibic_files") {
        const file = await fetchCNAuBIBICFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_ub_pixe_files") {
        const file = await fetchCNAuBPIXEFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
      if (modelKey === "cna_ub_tribic_files") {
        const file = await fetchCNAuBTRIBICFileById(fileId);
        setSelectedFile(file);
        setDetailDialogOpen(true);
      }
    } catch (error) {
      console.error("Error loading file detail:", error);
    }
  };

  const closeFileDetail = () => {
    setSelectedFile(null);
    setDetailDialogOpen(false);
  };

  const exportHandlersByModel = {
    cmam_toferda_files: exportCMAMTOFERDAFilesById,
    cna_std_erda_files: exportCNASTDERDAFilesById,
    cna_std_nra_files: exportCNASTDNRAFilesById,
    cna_std_pige_files: exportCNASTDPIGEFilesById,
    cna_std_pixe_files: exportCNASTDPIXEFilesById,
    cna_std_rbs_files: exportCNASTDRBSFilesById,
    cna_ub_ibic_files: exportCNAuBIBICFilesById,
    cna_ub_pixe_files: exportCNAuBPIXEFilesById,
    cna_ub_tribic_files: exportCNAuBTRIBICFilesById,
  };

  const fetchFilesByModelKey = async (modelKey) => {
    const fetchFn = fileFetchByModelKey[modelKey];
    if (!fetchFn) return;

    try {
      const response = await fetchFn(proposal.id);
      const files = response.data || response;

      console.log("FETCH FILES BY MODEL KEY:", modelKey, files);

      setFilesByType((prev) => ({
        ...prev,
        [modelKey]: (files || []).filter((f) => f.proposal?.id === proposal.id), // 🔹 filtra por proposal
      }));
    } catch (err) {
      console.error("Error updating file list:", err);
    }
  };

  const fetchAllFilesByProposal = async () => {
    if (!proposal?.id) return;

    const newFilesByType = {};

    await Promise.all(
      availableModels.map(async (modelKey) => {
        const fetchFn = fileListByModelKeyAndProposal[modelKey];
        if (!fetchFn) return;

        try {
          const response = await fetchFn(proposal.id);
          const files = response.data || response;
          newFilesByType[modelKey] = normalizeIfNeeded(modelKey, files);
        } catch (err) {
          console.error(`Error fetching ${modelKey} files:`, err);
        }
      }),
    );

    setFilesByType(newFilesByType);
  };

  const handleExportProposal = async () => {
    try {
      const response = await exportProposalsById([proposal.id]);
      const blob = new Blob([response], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${proposal.code}_metadata.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting proposal:", err);
    }
  };

  const deleteHandlersByModel = {
    cmam_toferda_files: softDeleteCMAMTOFERDAFile,
    cna_std_erda_files: softDeleteCNASTDERDAFile,
    cna_std_nra_files: softDeleteCNASTDNRAFile,
    cna_std_pige_files: softDeleteCNASTDPIGEFile,
    cna_std_pixe_files: softDeleteCNASTDPIXEFile,
    cna_std_rbs_files: softDeleteCNASTDRBSFile,
    cna_ub_ibic_files: softDeleteCNAuBIBICFile,
    cna_ub_pixe_files: softDeleteCNAuBPIXEFile,
    cna_ub_tribic_files: softDeleteCNAuBTRIBICFile,
  };

  const handleExportSelectedFiles = async () => {
    try {
      const fileIds = selectedFilesByModel[activeTab] || [];
      if (fileIds.length === 0) return;

      const exportFunction = exportHandlersByModel[activeTab];
      if (!exportFunction)
        throw new Error(`No export function for model: ${activeTab}`);

      const response = await exportFunction(fileIds);
      const blob = new Blob([response], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${proposal.code}_metadata.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting selected files:", err);
    }
  };

  const handleDeleteSelectedFiles = async () => {
    try {
      const fileIds = selectedFilesByModel[activeTab] || [];
      if (fileIds.length === 0) return;

      if (
        !window.confirm("Are you sure you want to delete the selected files?")
      )
        return;

      const deleteFunction = deleteHandlersByModel[activeTab];
      if (!deleteFunction)
        throw new Error(`No delete function for model: ${activeTab}`);

      await Promise.all(fileIds.map((id) => deleteFunction(id)));

      // Actualiza el estado eliminando los archivos borrados
      const updatedFiles = filesByType[activeTab].filter(
        (file) => !fileIds.includes(file.id),
      );
      setFilesByType((prev) => ({ ...prev, [activeTab]: updatedFiles }));
      setSelectedFilesByModel((prev) => ({ ...prev, [activeTab]: [] }));
    } catch (err) {
      console.error("Error deleting selected files:", err);
    }
  };

  // limpiar campos que no deben heredarse y preparar defaults
  const buildInitialValuesFromLast = (modelKey, last) => {
    const defaultUnits = {
      beam_energy_units: "MeV",
      beam_current_units: "nA",
      // injection_energy_units: "MeV",
      terminal_voltage_units: "MV",
      magnetic_field_units: "G",
      electromagnet_current_units: "A",
      beam_shape_diameter_units: "mm",
      beam_shape_width_units: "mm",
      beam_shape_height_units: "mm",
      incident_angle_units: "deg",
      exit_angle_units: "deg",
      scattering_angle_units: "deg",
      filter_thickness_units: "mm",
      filter_diameter_units: "mm",
      detector_BIAS_voltage_units: "V",
      shaping_time_units: "ns",
      peaking_time_units: "ns",
      accumulated_charge_units: "uC",
      detector_inverse_current_units: "nA",
      sample_TOF_detector_distance_units: "cm",
      sample_detector_distance_units: "cm",
      length_of_flight_units: "cm",
      TOF_station_BIAS_voltage_units: "V",
      TOF_TAC_range_units: "ns",
      coincidence_window_units: "ns",
      energy_detector_BIAS_voltage_units: "V",
      sample_energy_detector_distance_units: "cm",
      energy_shaping_time_units: "ns",
      scan_size_units: "um",
      time_per_pixel_units: "us/px",
    };

    if (!last) {
      return {
        proposal: proposal?.id || null,
        name: "",
        sample_ID: "",
        ...defaultUnits,
      };
    }

    const {
      id,
      created_at,
      updated_at,
      deleted_at,
      created_by,
      deleted_by,
      ...rest
    } = last;

    const normalize = (v) =>
      v && typeof v === "object" && "id" in v ? v.id : v;

    const normalized = { ...rest };
    if ("beam_isotope" in normalized)
      normalized.beam_isotope = normalize(normalized.beam_isotope);
    if ("detector" in normalized)
      normalized.detector = normalize(normalized.detector);
    if ("reaction" in normalized)
      normalized.reaction = normalize(normalized.reaction);
    if ("TOF_detector" in normalized)
      normalized.TOF_detector = normalize(normalized.TOF_detector);
    if ("energy_detector" in normalized)
      normalized.energy_detector = normalize(normalized.energy_detector);

    return {
      ...defaultUnits, // prioridad menor: se sobrescriben si vienen de `last`
      ...normalized,
      proposal: proposal?.id || null,
      name: "",
    };
  };

  const handleOpenAdd = async () => {
    const modelKey = activeTab;
    const last = await loadLastFileByModelKey(modelKey, proposal?.id);
    const defaults = buildInitialValuesFromLast(modelKey, last);
    setAddModelKey(modelKey);
    setAddInitialValues(defaults);
    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
    setAddModelKey(null);
    setAddInitialValues(null);
  };

  const handleFileSubmit = async (modelKey, result) => {
    try {
      // Ya está creado en FileDialog → no vuelvas a crear
      handleCloseAdd();
    } catch (error) {
      console.error("Error post-creation:", error);
    }
  };

  useEffect(() => {
    const loadProposal = async () => {
      try {
        const data = await fetchProposalById(id);
        setProposal(data);

        const beamlines = data.beamlines?.map((b) => b.code) || [];
        const techniques = data.techniques?.map((t) => t.alias) || [];
        const validModels = Array.from(
          new Set(
            beamlines
              .flatMap((bl) => beamlineToModels[bl] || [])
              .filter((model) => techniques.includes(modelLabels[model])),
          ),
        );

        const detectedFiles = {};
        validModels.forEach((modelKey) => {
          const raw = (data[modelKey] || []).filter((file) => !file.is_deleted);
          let finalFiles = raw;

          if (modelKey === "cna_ub_pixe_files") {
            console.log("ANTES:", raw[0]);
            const normalized = normalizeFilesUnits(raw);
            console.log("DESPUÉS:", normalized[0]);
            finalFiles = normalized;
          }

          detectedFiles[modelKey] = finalFiles;
        });

        setFilesByType(detectedFiles);
        setAvailableModels(validModels);
        setActiveTab(validModels[0] || null);
      } catch (err) {
        console.error("Error loading proposal:", err);
      }
    };

    loadProposal();
  }, [id]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box p={3}>
        {proposal && (
          <>
            <Card
              sx={{
                mb: 4,
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "#ffffffcc",
                backdropFilter: "blur(4px)",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={3}
                pt={2}
                pb={1}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "primary" }}
                >
                  {`${proposal.code}: ${proposal.title}`}
                </Typography>

                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleOpenAdd}
                    disabled={!activeTab}
                    sx={{
                      color: "#fff", // fuerza texto blanco
                      "&:hover": {
                        backgroundColor: "#218838", // tono de hover (mismo que usas en tus estilos .css)
                      },
                    }}
                  >
                    Add {modelLabels[activeTab] || ""} File
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleExportProposal}
                  >
                    Export proposal
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    disabled={
                      !selectedFilesByModel[activeTab] ||
                      selectedFilesByModel[activeTab].length === 0
                    }
                    onClick={handleExportSelectedFiles}
                  >
                    Export selected files
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="error"
                    disabled={
                      !selectedFilesByModel[activeTab] ||
                      selectedFilesByModel[activeTab].length === 0
                    }
                    onClick={handleDeleteSelectedFiles}
                  >
                    Delete selected files
                  </Button>
                </Box>
              </Box>
              <CardContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Abstract:</strong>
                </Typography>

                {proposal.abstract.split(/\r?\n/).map((line, index) => (
                  <Typography key={index} variant="body2" paragraph>
                    {line}
                  </Typography>
                ))}

                <Divider
                  sx={{ mt: 2, borderColor: "#b0bec5", borderBottomWidth: 2 }}
                />

                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Submission date:</strong>{" "}
                    {new Date(proposal.submission_date)
                      .toISOString()
                      .slice(0, 10)}
                  </Typography>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2">
                    <strong>Institution:</strong> {proposal.institution?.alias}
                  </Typography>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2">
                    <strong>Infrastructure:</strong>{" "}
                    {proposal.infrastructure?.name}
                  </Typography>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2">
                    <strong>Beamlines:</strong>{" "}
                    {proposal.beamlines?.map((b) => b.code).join(", ") || "—"}
                  </Typography>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2">
                    <strong>Techniques:</strong>{" "}
                    {proposal.techniques?.map((t) => t.alias).join(", ") || "—"}
                  </Typography>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2">
                    <strong>PI:</strong>{" "}
                    {proposal.pi?.name && proposal.pi?.surname
                      ? `${proposal.pi.name} ${proposal.pi.surname}`
                      : "—"}
                  </Typography>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2">
                    <strong>Researchers:</strong>{" "}
                    {proposal.source === "manual"
                      ? proposal.participants_text || "—"
                      : proposal.researchers?.length > 0
                      ? proposal.researchers
                          .map((r) =>
                            `${r.name ?? ""} ${r.surname ?? ""}`.trim(),
                          )
                          .join(", ")
                      : "—"}
                  </Typography>

                  <Divider orientation="vertical" flexItem />

                  <Typography variant="body2">
                    <strong>Projects:</strong>{" "}
                    {proposal.source === "manual"
                      ? proposal.projects_text || "—"
                      : proposal.projects?.length > 0
                      ? proposal.projects.map((p) => p.title).join(", ")
                      : "—"}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                mb: 2,
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "#ffffffcc",
                backdropFilter: "blur(4px)",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Associated Files
                </Typography>

                <Tabs
                  value={activeTab}
                  onChange={(e, val) => setActiveTab(val)}
                  variant="scrollable"
                  scrollButtons="auto"
                  TabIndicatorProps={{
                    style: { display: "none" }, // ⛔️ oculta el indicador
                  }}
                  sx={{
                    backgroundColor: "#ffffffcc",
                    borderRadius: "8px",
                    px: 2,
                    minHeight: "48px",
                  }}
                >
                  {availableModels.map((modelKey) => (
                    <Tab
                      key={modelKey}
                      value={modelKey}
                      label={modelLabels[modelKey] || modelKey}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                        color: "#000",
                        minHeight: "48px",
                        border: "1px solid #e0e0e0",
                        mx: 1,
                        backgroundColor:
                          activeTab === modelKey ? "#e3f2fd" : "transparent", // 👈 fondo para activa
                        "&.Mui-selected": {
                          color: "#000",
                        },
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                        },
                      }}
                    />
                  ))}
                </Tabs>
              </CardContent>
            </Card>

            {availableModels.map(
              (modelKey) =>
                activeTab === modelKey && (
                  <DataGrid
                    key={modelKey}
                    rows={filesByType[modelKey] || []}
                    columns={attachUnitsTogethers(
                      modelKey,
                      getColumnsForModel(modelKey, openFileDetail),
                    )}
                    domLayout="auto"
                    getRowId={(row) => row.id}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onRowSelectionModelChange={(newSelection) => {
                      setSelectedFilesByModel((prev) => ({
                        ...prev,
                        [modelKey]: newSelection.map(Number),
                      }));
                    }}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 15, page: 0 }, // ✅ aplica correctamente el tamaño de página
                      },
                      sorting: {
                        sortModel: [{ field: "created_at", sort: "desc" }],
                      },
                    }}
                    pageSizeOptions={[15, 25, 50]} // ✅ nueva propiedad (en lugar de rowsPerPageOptions)
                    columnVisibilityModel={{
                      created_at: false,
                    }}
                    sx={{
                      backgroundColor: "#fff", // Fondo de las celdas
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",

                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#e3f2fd", // 💠 Celeste claro (puedes ajustar)
                        color: "#000000", // Texto azul oscuro opcional
                        fontWeight: "bold",
                      },

                      "& .MuiDataGrid-cell": {
                        color: "#333", // Color del texto de las celdas
                      },

                      "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#f5f5f5", // Efecto hover suave
                      },

                      "& .MuiDataGrid-selectedRowCount": {
                        visibility: "hidden", // Opcional: oculta el contador de seleccionados si no lo usas
                      },
                    }}
                  />
                ),
            )}

            <FileDetailView
              open={detailDialogOpen}
              onClose={closeFileDetail}
              file={selectedFile}
              modelKey={activeTab}
              onFileUpdated={() => fetchFilesByModelKey(activeTab)}
            />

            <FileDialog
              open={addOpen}
              modelKey={addModelKey}
              mode="create" // ⬅️ explícito, aunque es opcional
              initialValues={addInitialValues}
              onCancel={handleCloseAdd}
              onSubmit={handleFileSubmit}
              onCreated={() => {
                fetchAllFilesByProposal();
              }}
              modelLabels={modelLabels}
            />
          </>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ProposalDetailPage;
